import { ArrowIcon } from 'components/icons/ArrowIcon';
import { useLanguage } from 'context/language.context';
import { cn } from 'lib/utils';
import { motion } from 'motion/react';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';

interface CardSliderProps {
  data: {
    label: string;
    description: string;
    content: ReactNode;
  }[];
  slidesShown?: number;
}

export const CardSlider: FC<CardSliderProps> = ({ data, slidesShown = 4 }) => {
  const { direction } = useLanguage();

  const parentRef = useRef<HTMLDivElement>(null);

  const [width, setWidth] = useState<number>(0);
  const [currentSlide, setCurrentSlide] = useState<number>(0);

  useEffect(() => {
    const parentDiv = parentRef.current;
    if (!parentDiv) return;

    const resizeObserver = new ResizeObserver(() => {
      const windowWidth = window.innerWidth;
      const parentWidth = parentDiv.offsetWidth;
      const padding = windowWidth >= 768 ? 224 : windowWidth >= 640 ? 128 : 32;
      if (slidesShown > 1) {
        setWidth((parentWidth - padding - 32 * (slidesShown - 1)) / slidesShown);
      } else {
        setWidth(parentWidth * 0.85 - padding);
      }
    });

    resizeObserver.observe(parentDiv);

    return () => {
      resizeObserver.disconnect();
    };
  }, [slidesShown]);

  return (
    <div ref={parentRef} className="w-full">
      <div className="relative w-full flex gap-4 sm:gap-8 h-[500px] overflow-hidden">
        {data.map(({ label, description, content }, index) => (
          <div
            key={index}
            className="relative duration-200"
            style={{
              width: `${width}px`,
              maxWidth: `${width}px`,
              minWidth: `${width}px`,
              transform: `translateX(${direction === 'ltr' ? '-' : ''}${currentSlide * 100 * (1 + (window.innerWidth >= 640 ? 31 : 15) / width)}%)`,
            }}
          >
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{
                opacity: 1,
                transition: { ease: 'easeOut', duration: 0.6, delay: 0.1 + 0.2 * index },
              }}
              whileHover={{
                opacity: 1,
                rotate: -4,
                transition: { ease: 'easeOut', duration: 0.3, delay: 0 },
              }}
              transition={{ type: 'tween', duration: 0.3, delay: 0 }}
              className={`relative flex w-full h-full flex-col border border-[#333333] rounded-2xl overflow-hidden`}
              style={
                {
                  //width: `${width}px`,
                  //maxWidth: `${width}px`,
                  //minWidth: `${width}px`,
                  //x: `${direction === 'ltr' ? '-' : ''}${currentSlide * 100 * (1 + (window.innerWidth >= 640 ? 32 : 16) / width)}%`,
                }
              }
            >
              <div className="min-h-[68px] mt-4 px-4">
                <p className="text-sm text-gray-600">{label}</p>
                <p className="font-semibold">{description}</p>
              </div>
              <div className="flex flex-1 mt-2">{content}</div>
            </motion.div>
          </div>
        ))}
      </div>
      <div
        className={cn(
          'relative flex justify-end gap-3 mt-8',
          direction === 'rtl' ? 'left-8 sm:left-32 md:left-56' : 'right-8 sm:right-32 md:right-56',
        )}
      >
        <button
          className={cn(
            'p-2.5 rounded-full border border-[#333333] bg-[linear-gradient(144.42deg,#151515_0%,#101010_100%)]',
            currentSlide === 0 && 'opacity-40',
            direction === 'ltr' && 'scale-x-[-1]',
          )}
          onClick={() => setCurrentSlide((prev) => (prev > 0 ? prev - 1 : prev))}
        >
          <ArrowIcon />
        </button>
        <button
          className={cn(
            'p-2.5 rounded-full border border-[#333333] bg-[linear-gradient(144.42deg,#151515_0%,#101010_100%)]',
            currentSlide + slidesShown === data.length && 'opacity-40',
            direction === 'rtl' && 'scale-x-[-1]',
          )}
          onClick={() =>
            setCurrentSlide((prev) => (prev + slidesShown < data.length ? prev + 1 : prev))
          }
        >
          <ArrowIcon />
        </button>
      </div>
    </div>
  );
};
